import type { NavColumnItem, NavGroup } from './types';

/**
 * Orders the navigation without mutating the fetched payload.
 *
 * `headerData` is a cached module-level array shared by every render on the
 * server, so the in-place `.sort()` this replaces reordered the same object the
 * next request was about to read.
 */
export const sortNavGroups = (groups?: NavGroup[] | null): NavGroup[] =>
  [...(groups ?? [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

/** A group opens a panel only when it has column items and no destination. */
export const isPanelGroup = (group: NavGroup): boolean =>
  (group.items?.length ?? 0) > 0 && !group.slug;

/** A section reveals a sub-panel when it is a trigger rather than a link. */
export const hasSubpanel = (item: NavColumnItem): boolean =>
  !item.slug && (item.clickedItems?.length ?? 0) > 0;

const toDomToken = (title: string): string =>
  title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Identity of one navigation entry.
 *
 * Titles alone are not safe to key on: nothing in Strapi stops two entries
 * sharing one, and two entries with the same key would open together and emit
 * duplicate DOM ids into the `aria-controls` wiring. The position disambiguates
 * them, and is identical on the server and the client because both read the
 * same ordered array.
 */
export const navKey = (title: string, index: number): string =>
  `${index}-${toDomToken(title)}`;

export const navPanelId = (groupKey: string): string => `nav-panel-${groupKey}`;

export const navTriggerId = (groupKey: string): string =>
  `nav-trigger-${groupKey}`;

export const navSubpanelId = (groupKey: string, itemKey: string): string =>
  `nav-subpanel-${groupKey}-${itemKey}`;

export const mobileNavLevelId = (levelKey: string): string =>
  `mobile-nav-level-${levelKey}`;

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);
